import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Calendar, Sparkles, Loader2 } from 'lucide-react';
import { TiltCard } from './TiltCard';
import { ScrollDepthReveal } from './ScrollDepthReveal';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogSection = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/blogs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setBlogPosts(data.slice(0, 4)))
      .catch(() => setBlogPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  if (loading) {
    return (
      <section className="relative py-20 flex justify-center bg-[#0a0a0c]">
        <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
      </section>
    );
  }

  if (blogPosts.length === 0) return null;

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden bg-[#0a0a0c]">
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-4">
            <Sparkles size={14} className="mr-2" />Latest Insights
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            Blog Section
          </h2>
          <p className="text-neutral-400 mt-4 max-w-2xl mx-auto">
            Insights, thoughts, and stories on business, technology, and life
          </p>
        </div>

        <ScrollDepthReveal>
          <Link to={`/blog/${blogPosts[0].slug}`} className="group block mb-12" data-testid="home-blog-featured-link">
            <TiltCard maxTilt={8} hoverScale={1.02} testId="home-blog-featured-tilt">
              <div className="absolute -inset-1 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-3xl blur-lg opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-white/[0.04] backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden group-hover:shadow-2xl group-hover:shadow-black/50 transition-all duration-500">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-64 lg:h-auto overflow-hidden">
                    <img src={blogPosts[0].featured_image} alt={blogPosts[0].featured_image_alt || blogPosts[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40" />
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-2 bg-white text-black text-sm font-semibold rounded-full shadow-lg shadow-black/30">
                        Featured
                      </span>
                    </div>
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center text-neutral-400 text-sm mb-4">
                      <span className="px-3 py-1 bg-white/10 text-neutral-200 rounded-full text-xs font-medium mr-3">
                        {blogPosts[0].category}
                      </span>
                      <Calendar size={14} className="mr-1" />{formatDate(blogPosts[0].created_at)}
                      <span className="mx-2">•</span>
                      <Clock size={14} className="mr-1" />{blogPosts[0].read_time}
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-neutral-400 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {blogPosts[0].title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-neutral-300">{blogPosts[0].author_display || blogPosts[0].author_name}</span>
                      <span className="inline-flex items-center text-white font-semibold group-hover:text-neutral-300 transition-colors">
                        Read More<ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </TiltCard>
          </Link>
        </ScrollDepthReveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogPosts.slice(1).map((post, index) => (
            <ScrollDepthReveal key={post.blog_id} delay={Math.min(index * 0.08, 0.24)}>
              <Link to={`/blog/${post.slug}`} className="group block" data-testid={`home-blog-tile-${post.slug}`}>
                <TiltCard maxTilt={14} hoverScale={1.06} testId={`home-blog-tile-tilt-${post.slug}`} className="h-full">
                  <div className="absolute -inset-1 bg-gradient-to-r from-white/15 via-white/10 to-white/15 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative h-full bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden group-hover:border-white/30 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-black/50">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-2xl pointer-events-none" />
                    <div className="relative h-48 overflow-hidden">
                      <img src={post.featured_image} alt={post.featured_image_alt || post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <span className="absolute bottom-3 left-3 px-3 py-1 bg-black/60 backdrop-blur-sm text-white text-xs font-medium rounded-full border border-white/20">
                        {post.category}
                      </span>
                    </div>
                    <div className="p-5" style={{ transform: 'translateZ(30px)' }}>
                      <div className="flex items-center text-neutral-500 text-xs mb-3">
                        <Calendar size={12} className="mr-1" />{formatDate(post.created_at)}
                        <span className="mx-2">•</span>
                        <Clock size={12} className="mr-1" />{post.read_time}
                      </div>
                      <h3 className="text-lg font-bold text-white mb-3 line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-neutral-400 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                        {post.title}
                      </h3>
                      <div className="flex items-center justify-between pt-4 border-t border-white/10">
                        <span className="text-xs text-neutral-400">{post.author_display || post.author_name}</span>
                        <span className="text-white text-sm font-medium group-hover:text-neutral-300 transition-colors flex items-center">
                          Read <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </TiltCard>
              </Link>
            </ScrollDepthReveal>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/blog" className="group inline-flex items-center px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white font-semibold rounded-full hover:bg-white hover:text-black hover:border-transparent transition-all duration-300 hover:shadow-lg hover:shadow-white/20">
            View All Posts<ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
